import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Autocomplete, TextField } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import CloseIcon from '@mui/icons-material/Close'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import styled from 'styled-components'
import {
  normalizePostcode,
  parsePostcodes,
  UK_POSTCODE_PATTERN
} from './manualPostcodeLookup'

const SKIP = '__skip__'
const CREATE = '__create__'
const ROW_BATCH_SIZE = 80
const unitNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base'
})

const compareUnitNames = (left, right) =>
  unitNameCollator.compare(String(left || ''), String(right || ''))

const buildSearchableText = values =>
  values.flat().filter(Boolean).join(' ').toLowerCase()

const getApiKey = item =>
  item?.api_unit_key || (item?.uprn ? `uprn:${item.uprn}` : item?.address || '')

// Dotted digits are one compound floor.unit identifier (OS names flat 4 on
// floor 2 as "2.4"), so they are captured whole — "2.4" is not "2" or "4".
const unitNumber = name => {
  if (!name) return ''
  const typed = name.match(
    /\b(?:flat|apartment|apt|unit|suite|room|studio|townhouse|house|plot)\s*#?\s*([a-z]{0,2}[0-9]+(?:\.[0-9]+)*[a-z]?)\b/i
  )
  if (typed) return typed[1].toLowerCase()
  const any = name.match(/\b([a-z]{0,2}[0-9]+(?:\.[0-9]+)*[a-z]?)\b/)
  return any ? any[1].toLowerCase() : ''
}

const getUnitDisplayName = unit => {
  const name = String(unit?.unit_name || '').trim()

  if (!name) return 'Unnamed OS address'

  const [unitIdentifier] = name.split(',')
  return unitIdentifier.trim() || name
}

const BUILDING_TYPE_LABELS = {
  residential: 'Residential',
  'residential.dwelling.sole_unit': 'Residential',
  office: 'Office',
  retail: 'Retail',
  assembly: 'Assembly',
  recreation: 'Recreation',
  leisure: 'Leisure',
  industrial: 'Industrial',
  storage: 'Storage',
  car_parks: 'Car parks',
  public: 'Public',
  hotel: 'Hotel',
  infrastructure: 'Infrastructure',
  labs: 'Labs'
}

const getBuildingTypeLabel = unit => {
  const type = unit?.building_type || unit?.buildingType
  return (
    unit?.building_type_label ||
    unit?.buildingTypeLabel ||
    BUILDING_TYPE_LABELS[type] ||
    null
  )
}

const STATUS_LABEL = {
  correct: 'UPRN match',
  mismatch: 'Number mismatch',
  assign: 'Assign UPRN',
  unmatched: 'No saved unit match',
  fallback: 'UPRN only',
  create: 'New unit',
  conflict: 'Conflict',
  ignored: 'Ignored'
}

const isReviewRow = row => row.status === 'mismatch' || row.conflicted

const UnitsReconciliationModal = ({
  open,
  onClose,
  onApplySelectedMatches,
  onConfirmReview,
  onLookupPostcodes,
  onUpdateIgnored,
  onUsePublicData,
  preferPublicData = false,
  reconciliationData,
  loading,
  postcodeLookupLoading = false
}) => {
  const [decisions, setDecisions] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [reviewStep, setReviewStep] = useState(false)
  const [publicDataStep, setPublicDataStep] = useState(false)
  const [listView, setListView] = useState('attention')
  const [visibleLimit, setVisibleLimit] = useState(ROW_BATCH_SIZE)
  const [postcodeInput, setPostcodeInput] = useState('')
  const [postcodeError, setPostcodeError] = useState('')
  const [updatingIgnored, setUpdatingIgnored] = useState(false)
  const lastResetKeyRef = useRef('')

  const apiUnits = Array.isArray(reconciliationData?.api_units)
    ? reconciliationData.api_units
    : []
  const existingUnits = Array.isArray(reconciliationData?.existing_units)
    ? reconciliationData.existing_units
    : []
  const preview = reconciliationData?.preview || null
  const summary = reconciliationData?.summary || {}
  const previewSummary = preview?.preview_result_summary || {}
  const manualPostcodeLimit = Number(previewSummary?.manual_postcode_limit || 5)
  const manualPostcodeTargetUprns = useMemo(() => {
    const eligibleUprns = new Set(
      apiUnits
        .filter(
          unit =>
            !unit?.is_ignored &&
            unit?.match_confidence !== 'high' &&
            !unit?.suggested_existing_unit_id
        )
        .map(unit => String(unit?.uprn || ''))
        .filter(Boolean)
    )
    const fromPreview = previewSummary?.manual_postcode_target_uprns
    if (Array.isArray(fromPreview)) {
      return Array.from(new Set(fromPreview.map(String).filter(Boolean))).filter(
        uprn => eligibleUprns.has(uprn)
      )
    }

    return Array.from(eligibleUprns)
  }, [apiUnits, previewSummary])
  const checkedPostcodes = Array.from(
    new Set((preview?.checked_postcodes || []).map(normalizePostcode).filter(Boolean))
  )
  const enteredPostcodes = parsePostcodes(postcodeInput)
  const invalidEnteredPostcode = enteredPostcodes.find(
    postcode => !UK_POSTCODE_PATTERN.test(postcode)
  )
  const uncheckedEnteredPostcodes = enteredPostcodes.filter(
    postcode => !checkedPostcodes.includes(postcode)
  )
  const alreadyCheckedEnteredPostcodes = enteredPostcodes.filter(postcode =>
    checkedPostcodes.includes(postcode)
  )
  const postcodeLimitExceeded = uncheckedEnteredPostcodes.length > manualPostcodeLimit
  const canSearchPostcodes =
    uncheckedEnteredPostcodes.length > 0 &&
    !invalidEnteredPostcode &&
    !postcodeLimitExceeded

  const existingById = useMemo(() => {
    const map = {}
    existingUnits.forEach(unit => {
      if (unit?.id) map[String(unit.id)] = unit
    })
    return map
  }, [existingUnits])

  const unitOptions = useMemo(
    () =>
      existingUnits
        .map(unit => ({
          id: String(unit.id),
          label: unit.name || 'Unnamed unit',
          uprn: unit.uprn || null
        }))
        .sort((left, right) => compareUnitNames(left.label, right.label)),
    [existingUnits]
  )

  const suggestedIdByKey = useMemo(() => {
    const map = {}
    apiUnits.forEach(api => {
      const key = getApiKey(api)
      if (api.suggested_existing_unit_id) {
        map[key] = String(api.suggested_existing_unit_id)
      }
    })
    return map
  }, [apiUnits])

  const optionsFor = key => {
    const suggestedId = suggestedIdByKey[key]
    const units = suggestedId
      ? [
        ...unitOptions.filter(o => o.id === suggestedId).map(o => ({ ...o, suggested: true })),
        ...unitOptions.filter(o => o.id !== suggestedId)
      ]
      : unitOptions
    return [
      { id: SKIP, label: 'Leave unchanged', action: true },
      { id: CREATE, label: 'Create a new unit', action: true },
      ...units
    ]
  }

  const isEmptyBp = existingUnits.length === 0

  const defaults = useMemo(() => {
    const map = {}
    apiUnits.forEach(api => {
      const key = getApiKey(api)
      if (
        api.suggested_match_type === 'exact_uprn' &&
        api.suggested_existing_unit_id
      ) {
        map[key] = String(api.suggested_existing_unit_id)
      } else if (isEmptyBp && api.uprn) {
        map[key] = CREATE
      } else {
        map[key] = SKIP
      }
    })
    return map
  }, [apiUnits, isEmptyBp])

  const resetKey = useMemo(
    () =>
      apiUnits.map(getApiKey).join('|') +
      '::' +
      existingUnits.map(unit => unit?.id || '').join('|'),
    [apiUnits, existingUnits]
  )

  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return
    lastResetKeyRef.current = resetKey
    setDecisions(defaults)
  }, [resetKey, defaults])

  useEffect(() => {
    if (!open) {
      setConfirming(false)
      setReviewStep(false)
      setPublicDataStep(false)
      setListView('attention')
      setVisibleLimit(ROW_BATCH_SIZE)
    }
  }, [open])

  const setDecision = (key, value) =>
    setDecisions(previous => ({ ...previous, [key]: value }))

  const unitUsage = useMemo(() => {
    const usage = {}
    Object.values(decisions).forEach(value => {
      if (value !== SKIP && value !== CREATE && value) {
        usage[value] = (usage[value] || 0) + 1
      }
    })
    return usage
  }, [decisions])

  const matches = useMemo(
    () =>
      apiUnits
        .filter(api => !api?.is_ignored)
        .map(api => ({ api, value: decisions[getApiKey(api)] }))
        .filter(({ value }) => value && value !== SKIP && value !== CREATE)
        .map(({ api, value }) => ({ unit_id: value, os_unit: api })),
    [apiUnits, decisions]
  )

  const createUprns = useMemo(
    () =>
      apiUnits
        .filter(
          api =>
            !api?.is_ignored &&
            decisions[getApiKey(api)] === CREATE &&
            api.uprn
        )
        .map(api => api.uprn),
    [apiUnits, decisions]
  )

  const updateCount = matches.length
  const createCount = createUprns.length
  const skipCount = Math.max(0, apiUnits.length - updateCount - createCount)
  const hasConflict = Object.values(unitUsage).some(count => count > 1)
  const totalActions = updateCount + createCount

  // "Use public data" rebuilds the unit list from the official addresses:
  // current matches keep their saved unit (assigned the UPRN and renamed to
  // the official name), every other official address becomes a new unit, and
  // saved units left without a UPRN are deleted.
  const savedUprnSet = useMemo(
    () =>
      new Set(
        existingUnits
          .map(unit => String(unit?.uprn || '').trim())
          .filter(Boolean)
      ),
    [existingUnits]
  )

  const publicDataCreateUprns = useMemo(
    () =>
      apiUnits
        .filter(api => {
          if (api?.is_ignored || !api?.uprn) return false
          if (savedUprnSet.has(String(api.uprn).trim())) return false
          const value = decisions[getApiKey(api)]
          return !value || value === SKIP || value === CREATE
        })
        .map(api => api.uprn),
    [apiUnits, decisions, savedUprnSet]
  )

  const matchedUnitIds = useMemo(
    () => new Set(matches.map(match => String(match.unit_id))),
    [matches]
  )

  const publicDataDeleteUnits = useMemo(
    () =>
      existingUnits
        .filter(
          unit =>
            !String(unit?.uprn || '').trim() &&
            !matchedUnitIds.has(String(unit?.id))
        )
        .sort((left, right) => compareUnitNames(left?.name, right?.name)),
    [existingUnits, matchedUnitIds]
  )

  const publicDataCreateCount = publicDataCreateUprns.length
  const publicDataDeleteCount = publicDataDeleteUnits.length
  const publicDataActionCount =
    updateCount + publicDataCreateCount + publicDataDeleteCount
  const showPublicDataOption =
    typeof onUsePublicData === 'function' && publicDataActionCount > 0

  // A Building Passport created with "use unit names from the public
  // address data" opens straight on the public-data outcome, so the
  // official names are what the user sees first. Back still reveals the
  // manual matching step. Applied once per open so Back is not undone.
  const autoPublicDataStepAppliedRef = useRef(false)
  useEffect(() => {
    if (!open) {
      autoPublicDataStepAppliedRef.current = false
      return
    }
    if (
      autoPublicDataStepAppliedRef.current ||
      !preferPublicData ||
      loading ||
      !showPublicDataOption
    ) {
      return
    }
    autoPublicDataStepAppliedRef.current = true
    setPublicDataStep(true)
  }, [open, preferPublicData, loading, showPublicDataOption])

  const classified = useMemo(() => {
    const order = { conflict: 0, mismatch: 1, assign: 2, fallback: 3, unmatched: 4, create: 5, correct: 6 }
    return apiUnits
      .map(api => {
        const key = getApiKey(api)
        const decision = decisions[key] ?? SKIP
        const matchType = api.suggested_match_type || null
        const suggestedId = suggestedIdByKey[key] || null
        const suggestedUnit = suggestedId ? existingById[suggestedId] : null

        let numberMismatch = false
        if (matchType === 'exact_uprn' && suggestedUnit) {
          const savedNumber = unitNumber(suggestedUnit.name)
          const osNumber = unitNumber(api.unit_name)
          numberMismatch = !!savedNumber && !!osNumber && savedNumber !== osNumber
        }

        let status
        if (matchType === 'exact_uprn') status = numberMismatch ? 'mismatch' : 'correct'
        else if (suggestedId) status = 'assign'
        else if (isEmptyBp && decision === CREATE) status = 'create'
        else if (api.match_reason === 'selected_os_uprn' || api.match_reason === 'uprn_postcodes') status = 'fallback'
        else status = 'unmatched'

        const chosen = decision !== SKIP && decision !== CREATE ? decision : null
        const conflicted = chosen ? unitUsage[chosen] > 1 : false

        return {
          api,
          key,
          decision,
          matchType,
          suggestedId,
          suggestedUnit,
          numberMismatch,
          status,
          conflicted,
          ignored: Boolean(api?.is_ignored)
        }
      })
      .sort((left, right) => {
        const lk = left.conflicted ? 'conflict' : left.status
        const rk = right.conflicted ? 'conflict' : right.status
        if (order[lk] !== order[rk]) return order[lk] - order[rk]
        return compareUnitNames(left.api.unit_name, right.api.unit_name)
      })
  }, [apiUnits, decisions, suggestedIdByKey, existingById, unitUsage])

  const readyRows = useMemo(
    () =>
      classified.filter(
        row =>
          !row.ignored &&
          row.suggestedId &&
          String(row.decision) === String(row.suggestedId) &&
          !isReviewRow(row) &&
          !row.conflicted
      ),
    [classified]
  )

  const readyKeys = useMemo(
    () => new Set(readyRows.map(row => row.key)),
    [readyRows]
  )

  const attentionRows = useMemo(
    () => classified.filter(row => !row.ignored && !readyKeys.has(row.key)),
    [classified, readyKeys]
  )

  const ignoredRows = useMemo(
    () => classified.filter(row => row.ignored),
    [classified]
  )

  const rowsForCurrentView = useMemo(
    () =>
      listView === 'matched'
        ? readyRows
        : listView === 'ignored'
          ? ignoredRows
          : attentionRows,
    [listView, readyRows, ignoredRows, attentionRows]
  )

  const normalizedSearch = searchQuery.trim().toLowerCase()
  const searchedRows = useMemo(
    () =>
      normalizedSearch
        ? rowsForCurrentView.filter(row =>
          buildSearchableText([
            row.api.unit_name,
            row.api.address,
            row.api.uprn,
            row.suggestedUnit?.name,
            existingById[row.decision]?.name
          ]).includes(normalizedSearch)
        )
        : rowsForCurrentView,
    [normalizedSearch, rowsForCurrentView, existingById]
  )

  const visibleRows = useMemo(
    () => searchedRows.slice(0, visibleLimit),
    [searchedRows, visibleLimit]
  )

  const loadMoreRowsIfNeeded = useCallback(event => {
    const list = event.currentTarget
    if (list.scrollHeight - list.scrollTop - list.clientHeight > 320) return

    setVisibleLimit(limit => Math.min(searchedRows.length, limit + ROW_BATCH_SIZE))
  }, [searchedRows.length])

  useEffect(() => {
    setVisibleLimit(ROW_BATCH_SIZE)
  }, [normalizedSearch, listView])

  const unappliedSuggestionCount = useMemo(
    () =>
      classified.filter(
        row =>
          !row.ignored &&
          row.suggestedId &&
          String(row.decision) !== String(row.suggestedId)
      ).length,
    [classified]
  )

  const totalSuggestionCount = useMemo(
    () => classified.filter(row => !row.ignored && row.suggestedId).length,
    [classified]
  )

  const applyAllSuggestions = () =>
    setDecisions(previous => {
      const next = { ...previous }
      classified.forEach(row => {
        if (!row.ignored && row.suggestedId) next[row.key] = String(row.suggestedId)
      })
      return next
    })

  const updateIgnoredRows = async (action, rows) => {
    const uprns = Array.from(
      new Set(rows.map(row => String(row.api?.uprn || '').trim()).filter(Boolean))
    )

    if (!uprns.length || typeof onUpdateIgnored !== 'function' || updatingIgnored) return

    setUpdatingIgnored(true)
    try {
      await onUpdateIgnored({ action, uprns })
      setSearchQuery('')
      setListView(action === 'restore' ? 'attention' : 'ignored')
    } finally {
      setUpdatingIgnored(false)
    }
  }

  const handleConfirm = async () => {
    if (hasConflict || totalActions === 0 || confirming) return
    setConfirming(true)
    try {
      if (typeof onConfirmReview === 'function') {
        await onConfirmReview({
          matches,
          selectedUprns: createUprns,
          previewContext: {
            nearbyExpanded: Boolean(preview?.nearby_expanded),
            nearbyRadiusIndex: Number.isInteger(preview?.nearby_radius_index)
              ? preview.nearby_radius_index
              : null
          }
        })
      } else if (matches.length && typeof onApplySelectedMatches === 'function') {
        await onApplySelectedMatches(matches)
      }
    } finally {
      setConfirming(false)
    }
  }

  const handleUsePublicData = async () => {
    if (hasConflict || publicDataActionCount === 0 || confirming) return
    if (typeof onUsePublicData !== 'function') return
    setConfirming(true)
    try {
      await onUsePublicData({
        matches,
        selectedUprns: publicDataCreateUprns,
        previewContext: {
          nearbyExpanded: Boolean(preview?.nearby_expanded),
          nearbyRadiusIndex: Number.isInteger(preview?.nearby_radius_index)
            ? preview.nearby_radius_index
            : null
        }
      })
    } finally {
      setConfirming(false)
    }
  }

  const handlePostcodeLookup = async () => {
    const postcodes = parsePostcodes(postcodeInput)

    if (!postcodes.length) {
      setPostcodeError('Enter at least one UK postcode.')
      return
    }

    const invalidPostcode = postcodes.find(postcode => !UK_POSTCODE_PATTERN.test(postcode))
    if (invalidPostcode) {
      setPostcodeError(`${invalidPostcode} is not a valid UK postcode.`)
      return
    }

    const uncheckedPostcodes = postcodes.filter(
      postcode => !checkedPostcodes.includes(postcode)
    )
    if (!uncheckedPostcodes.length) {
      setPostcodeError('Those postcodes have already been checked for this preview.')
      return
    }

    if (uncheckedPostcodes.length > manualPostcodeLimit) {
      setPostcodeError(`Search a maximum of ${manualPostcodeLimit} new postcodes at a time.`)
      return
    }

    setPostcodeError('')
    let result
    try {
      result = await onLookupPostcodes?.({
        postcodes: uncheckedPostcodes,
        targetUprns: manualPostcodeTargetUprns
      })
    } catch (error) {
      return
    }

    if (result?.matchedCount > 0) {
      setPostcodeInput('')
    }
  }

  const rawCandidatesCount = Number(summary?.api_units_count || apiUnits.length)
  const renderRow = row => {
    const { api, key, decision, status, suggestedUnit, conflicted } = row
    const badgeStatus = row.ignored ? 'ignored' : conflicted ? 'conflict' : status
    const options = optionsFor(key)
    const value = options.find(option => option.id === decision) || options[0]
    const typeLabel = getBuildingTypeLabel(api)
    const matchedUnit = decision !== SKIP && decision !== CREATE ? existingById[decision] : null
    const isGuess = matchedUnit && status === 'assign'
    const showUseSuggested = suggestedUnit && String(decision) !== String(suggestedUnit.id)

    return (
      <Row
        key={key}
        $flag={!row.ignored && (conflicted || status === 'mismatch') ? 'bad' : null}
      >
        <RowInfo>
          <RowTopLine>
            <RowTitle>{getUnitDisplayName(api)}</RowTitle>
            <StatusBadge $status={badgeStatus}>
              {STATUS_LABEL[badgeStatus] || STATUS_LABEL.unmatched}
            </StatusBadge>
          </RowTopLine>
          <RowAddress>{api.address || 'No address details available'}</RowAddress>
          <RowMeta>
            {typeLabel && <Pill>{typeLabel}</Pill>}
            <RowUprn>UPRN {api.uprn || '—'}</RowUprn>
          </RowMeta>
          {!row.ignored && status === 'mismatch' && suggestedUnit && (
            <RowWarn $error>
              Same property reference, different number — you call this <strong>{suggestedUnit.name}</strong>, while the address lists <strong>{api.unit_name}</strong>. Confirm which number is correct.
            </RowWarn>
          )}
        </RowInfo>

        <RowControl>
          {row.ignored ? (
            <RestoreAddressButton
              type='button'
              onClick={() => updateIgnoredRows('restore', [row])}
              disabled={updatingIgnored}
            >
              <RestoreRoundedIcon />
              Move back to review
            </RestoreAddressButton>
          ) : (
          <Autocomplete
            size='small'
            disableClearable
            selectOnFocus={false}
            options={options}
            value={value}
            onChange={(event, option) => setDecision(key, option?.id ?? SKIP)}
            noOptionsText='No saved units match your search'
            slotProps={{
              popper: {
                placement: 'bottom-end',
                modifiers: [
                  { name: 'flip', enabled: true },
                  { name: 'preventOverflow', options: { padding: 16 } }
                ],
                sx: {
                  zIndex: 100001,
                  width: '360px !important',
                  maxWidth: 'calc(100vw - 32px)',
                  minWidth: '0 !important'
                }
              },
              paper: {
                elevation: 6,
                sx: {
                  mt: 0.75,
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  boxShadow: '0 14px 34px rgba(15, 23, 42, 0.16)',
                  overflow: 'hidden'
                }
              },
              listbox: {
                sx: {
                  maxHeight: '280px',
                  padding: '6px',
                  '& .MuiAutocomplete-groupLabel': {
                    top: 0,
                    height: 'auto',
                    minHeight: '30px',
                    padding: '7px 10px 5px',
                    background: '#fff',
                    color: '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 700,
                    lineHeight: 1.3,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  },
                  '& .MuiAutocomplete-groupUl': { padding: 0 },
                  '& .MuiAutocomplete-option': {
                    minHeight: '42px',
                    margin: '2px 0',
                    padding: '8px 10px',
                    borderRadius: '7px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiAutocomplete-option[aria-selected="true"]': {
                    background: '#ecfeff'
                  },
                  '& .MuiAutocomplete-option.Mui-focused': {
                    background: '#f1f8fa'
                  }
                }
              }
            }}
            groupBy={option => (option.action ? 'Choose an action' : 'Use a saved unit')}
            getOptionLabel={option => (option.suggested ? `${option.label} — suggested` : option.label)}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            filterOptions={(opts, state) => {
              const q = state.inputValue.trim().toLowerCase()
              const filtered = !q ? opts : opts.filter(
                option =>
                  option.action ||
                  option.label.toLowerCase().includes(q) ||
                  String(option.uprn || '').includes(q)
              )
              const actions = filtered.filter(option => option.action)
              const savedUnits = filtered.filter(option => !option.action)
              return [...actions, ...savedUnits]
            }}
            renderOption={(props, option) => {
              const { key: optionKey, ...optionProps } = props
              return (
                <li key={optionKey} {...optionProps}>
                  <DropdownOption $action={option.action}>
                    <strong>{option.suggested ? `${option.label} — suggested` : option.label}</strong>
                    {!option.action && option.uprn && <span>UPRN {option.uprn}</span>}
                  </DropdownOption>
                </li>
              )
            }}
            renderInput={params => (
              <TextField {...params} placeholder='Choose what happens to this address…' />
            )}
          />
          )}
          {!row.ignored && showUseSuggested && (
            <UseSuggested type='button' onClick={() => setDecision(key, String(suggestedUnit.id))}>
              <CheckRoundedIcon style={{ fontSize: 15 }} />
              Use {suggestedUnit.name}
            </UseSuggested>
          )}
          {!row.ignored && conflicted && (
            <RowWarn $error>{matchedUnit?.name} is matched to more than one address — pick a different unit.</RowWarn>
          )}
          {!row.ignored && isGuess && !conflicted && (
            <RowWarn>Suggested from the address name. Check it against your address schedule before applying.</RowWarn>
          )}
        </RowControl>
      </Row>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={loading || confirming || updatingIgnored ? undefined : onClose}
      maxWidth={false}
      sx={{
        zIndex: 99999,
        '& .MuiDialog-paper': {
          backgroundColor: '#fff',
          borderRadius: '16px',
          width: 'min(1040px, calc(100vw - 32px))',
          height: 'min(1040px, calc(100vh - 24px))',
          maxWidth: '1040px',
          maxHeight: 'calc(100vh - 24px)',
          margin: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
          '@media (max-width: 760px)': {
            width: '100vw',
            height: '100vh',
            maxHeight: '100vh',
            margin: 0,
            borderRadius: 0,
            '@supports (height: 100dvh)': {
              height: '100dvh',
              maxHeight: '100dvh'
            }
          }
        }
      }}
    >
      <DialogHeader>
        <HeaderLeft>
          <DialogTitle>Review unit matches</DialogTitle>
        </HeaderLeft>
        <CloseButton type='button' onClick={onClose} disabled={loading || confirming || updatingIgnored}>
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <DialogBody onScroll={loadMoreRowsIfNeeded}>
        {!reconciliationData && loading ? (
          <Centered>
            <CircularProgress size={40} />
            <MutedText>Checking units for this Building Passport…</MutedText>
          </Centered>
        ) : !reconciliationData ? (
          <Centered>
            <MutedText>No preview data is available yet.</MutedText>
          </Centered>
        ) : rawCandidatesCount <= 0 ? (
          <Centered>
            <strong>No addresses found</strong>
            <MutedText>We couldn’t find any OS addresses to compare with this Building Passport.</MutedText>
            <PrimaryButton type='button' onClick={onClose}>Close</PrimaryButton>
          </Centered>
        ) : publicDataStep ? (
          <ReviewStep>
            <ReviewTitle>Use public data</ReviewTitle>
            <ReviewIntro>
              {preferPublicData
                ? 'This Building Passport is set to use unit names from the public address data, so the official names are shown straight away. Nothing changes until you press Use public data — press Back to match units manually instead.'
                : 'Your unit list will be rebuilt from the official address data. Nothing changes until you press Use public data.'}
            </ReviewIntro>
            <OutcomeList>
              <OutcomeRow>
                <OutcomeIcon $tone='success'><CheckRoundedIcon /></OutcomeIcon>
                <div>
                  <strong>{updateCount} saved {updateCount === 1 ? 'unit' : 'units'} will be matched</strong>
                  <span>Matched units keep their files and people, receive the official property reference and take the official unit name.</span>
                </div>
              </OutcomeRow>
              <OutcomeRow>
                <OutcomeIcon $tone='create'>+</OutcomeIcon>
                <div>
                  <strong>{publicDataCreateCount} new {publicDataCreateCount === 1 ? 'unit' : 'units'} will be created</strong>
                  <span>Every remaining official address becomes a new unit, named as it appears in the public data.</span>
                </div>
              </OutcomeRow>
              <OutcomeRow>
                <OutcomeIcon $tone='danger'>−</OutcomeIcon>
                <div>
                  <strong>{publicDataDeleteCount} saved {publicDataDeleteCount === 1 ? 'unit' : 'units'} without a property reference will be permanently deleted</strong>
                  <span>Any files, checklists and people attached to these units are removed with them. This cannot be undone.</span>
                  {publicDataDeleteCount > 0 && (
                    <DeleteNamesList>
                      {publicDataDeleteUnits.map(unit => (
                        <DeleteNameChip key={unit.id}>{unit.name || 'Unnamed unit'}</DeleteNameChip>
                      ))}
                    </DeleteNamesList>
                  )}
                </div>
              </OutcomeRow>
            </OutcomeList>
            {hasConflict && (
              <ReviewError>Two addresses point to the same saved unit. Go back and choose a different unit before applying.</ReviewError>
            )}
          </ReviewStep>
        ) : (
          !reviewStep ? (
            <ResolveStep>
              <ResolveControls>
                {manualPostcodeTargetUprns.length > 0 && (
                  <PostcodeLookupSection>
                  <PostcodeLookupCopy>
                    <strong>Find missing units by postcode</strong>
                    <span>
                      {manualPostcodeTargetUprns.length} unmatched UPRNs.
                      {checkedPostcodes.length > 0 && (
                        <> Checked: {checkedPostcodes.join(', ')}.</>
                      )}
                    </span>
                  </PostcodeLookupCopy>
                  <PostcodeLookupControls>
                    <PostcodeInput
                      type='text'
                      value={postcodeInput}
                      onChange={event => {
                        setPostcodeInput(event.target.value)
                        postcodeError && setPostcodeError('')
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          handlePostcodeLookup()
                        }
                      }}
                      placeholder='e.g. M1 2AB'
                      disabled={postcodeLookupLoading}
                    />
                    <LookupPostcodesButton
                      type='button'
                      onClick={handlePostcodeLookup}
                      disabled={postcodeLookupLoading || !canSearchPostcodes}
                      title={
                        enteredPostcodes.length > 0 && uncheckedEnteredPostcodes.length === 0
                          ? 'All entered postcodes have already been searched.'
                          : undefined
                      }
                    >
                      {postcodeLookupLoading ? (
                        <><CircularProgress size={14} style={{ color: '#fff' }} /> Searching…</>
                      ) : (
                        'Search postcode'
                      )}
                    </LookupPostcodesButton>
                  </PostcodeLookupControls>
                  {(enteredPostcodes.length > 0 || invalidEnteredPostcode || postcodeLimitExceeded) && (
                    <PostcodeHelper $error={Boolean(invalidEnteredPostcode || postcodeLimitExceeded)}>
                      {postcodeLimitExceeded
                        ? `Maximum ${manualPostcodeLimit} new postcodes per search.`
                        : invalidEnteredPostcode
                          ? 'Enter valid UK postcodes separated by commas.'
                          : uncheckedEnteredPostcodes.length === 0
                            ? `All ${alreadyCheckedEnteredPostcodes.length} already searched. Enter a different postcode.`
                            : `${uncheckedEnteredPostcodes.length} new postcode${uncheckedEnteredPostcodes.length === 1 ? '' : 's'}${alreadyCheckedEnteredPostcodes.length > 0 ? ` · ${alreadyCheckedEnteredPostcodes.length} already searched` : ''}.`}
                    </PostcodeHelper>
                  )}
                  {postcodeError && <PostcodeError>{postcodeError}</PostcodeError>}
                  </PostcodeLookupSection>
                )}
                {showPublicDataOption && (
                  <PublicDataSection>
                    <PublicDataCopy>
                      <strong>Prefer the official address list?</strong>
                      <span>
                        Rebuild your units from the public data: matched units
                        keep their files and people, the other official
                        addresses become new units, and saved units without a
                        property reference are deleted.
                      </span>
                    </PublicDataCopy>
                    <UsePublicDataButton
                      type='button'
                      data-tour-id='units.use-public-data'
                      onClick={() => setPublicDataStep(true)}
                      disabled={confirming || updatingIgnored}
                    >
                      Use public data…
                    </UsePublicDataButton>
                  </PublicDataSection>
                )}
              </ResolveControls>

              <RowsArea>
                <ViewTabs aria-label='Address review groups'>
                <ViewTab
                  type='button'
                  $active={!normalizedSearch && listView === 'attention'}
                  onClick={() => {
                    setSearchQuery('')
                    setListView('attention')
                  }}
                >
                  Needs review <span>{attentionRows.length}</span>
                </ViewTab>
                <ViewTab
                  type='button'
                  $active={!normalizedSearch && listView === 'matched'}
                  onClick={() => {
                    setSearchQuery('')
                    setListView('matched')
                  }}
                >
                  Matched units <span>{readyRows.length}</span>
                </ViewTab>
                <ViewTab
                  type='button'
                  $active={!normalizedSearch && listView === 'ignored'}
                  onClick={() => {
                    setSearchQuery('')
                    setListView('ignored')
                  }}
                >
                  Ignored <span>{ignoredRows.length}</span>
                </ViewTab>
                </ViewTabs>

                <ListTools>
                <SearchField>
                  <SearchRoundedIcon />
                  <input
                    type='search'
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    placeholder='Search by unit, address or UPRN'
                    aria-label='Search by unit, address or UPRN'
                  />
                  <SearchResultCount role='status' aria-live='polite'>
                    {normalizedSearch
                      ? `${searchedRows.length} found`
                      : `${classified.length} total`}
                  </SearchResultCount>
                </SearchField>
                {listView === 'attention' && attentionRows.length > 0 && (
                  <IgnoreAllButton
                    type='button'
                    onClick={() => updateIgnoredRows('ignore', attentionRows)}
                    disabled={updatingIgnored}
                  >
                    <VisibilityOffOutlinedIcon />
                    {updatingIgnored ? 'Saving…' : `Ignore all (${attentionRows.length})`}
                  </IgnoreAllButton>
                )}
                {listView === 'ignored' && ignoredRows.length > 0 && (
                  <RestoreAllButton
                    type='button'
                    onClick={() => updateIgnoredRows('restore', ignoredRows)}
                    disabled={updatingIgnored}
                  >
                    <RestoreRoundedIcon />
                    {updatingIgnored ? 'Saving…' : `Restore all (${ignoredRows.length})`}
                  </RestoreAllButton>
                )}
                {listView !== 'ignored' && unappliedSuggestionCount > 0 ? (
                  <ApplySuggestionsButton type='button' onClick={applyAllSuggestions}>
                    <CheckRoundedIcon />
                    Apply all suggestions ({unappliedSuggestionCount})
                  </ApplySuggestionsButton>
                ) : listView !== 'ignored' && totalSuggestionCount > 0 ? (
                  <SuggestionsApplied>
                    <CheckRoundedIcon />
                    All suggestions applied ({totalSuggestionCount})
                  </SuggestionsApplied>
                ) : null}
                </ListTools>
                <RowList onScroll={loadMoreRowsIfNeeded}>
                  {searchedRows.length === 0 ? (
                    <EmptyRow>
                      {normalizedSearch
                        ? 'No units, addresses or UPRNs match your search.'
                        : listView === 'ignored'
                          ? 'No addresses have been ignored.'
                          : 'Nothing needs review in this group.'}
                    </EmptyRow>
                  ) : (
                    visibleRows.map(renderRow)
                  )}
                </RowList>
              </RowsArea>
            </ResolveStep>
          ) : (
            <ReviewStep>
              <ReviewTitle>Ready to update your units</ReviewTitle>
              <ReviewIntro>Check the outcome below. Nothing changes until you press Apply changes.</ReviewIntro>
              <OutcomeList>
                <OutcomeRow>
                  <OutcomeIcon $tone='success'><CheckRoundedIcon /></OutcomeIcon>
                  <div><strong>{updateCount} saved {updateCount === 1 ? 'unit' : 'units'} will be matched</strong><span>These units will receive their confirmed property reference.</span></div>
                </OutcomeRow>
                <OutcomeRow>
                  <OutcomeIcon $tone='create'>+</OutcomeIcon>
                  <div><strong>{createCount} new {createCount === 1 ? 'unit' : 'units'} will be created</strong><span>New units use the address details shown in the previous step.</span></div>
                </OutcomeRow>
                <OutcomeRow>
                  <OutcomeIcon>—</OutcomeIcon>
                  <div><strong>{skipCount} {skipCount === 1 ? 'address stays' : 'addresses stay'} unchanged</strong><span>No data will be imported for these addresses.</span></div>
                </OutcomeRow>
              </OutcomeList>
              {hasConflict && (
                <ReviewError>Two addresses point to the same saved unit. Go back and choose a different unit before applying.</ReviewError>
              )}
            </ReviewStep>
          )
        )}
      </DialogBody>

      {!loading && reconciliationData && rawCandidatesCount > 0 && (
        <DialogFooter>
          <FooterInfo>
            {publicDataStep ? (
              <><strong>{updateCount}</strong> matched · <strong>{publicDataCreateCount}</strong> created · <strong>{publicDataDeleteCount}</strong> deleted</>
            ) : !reviewStep ? (
              <><strong>{attentionRows.length}</strong> to review · <strong>{readyRows.length}</strong> ready · <strong>{ignoredRows.length}</strong> ignored</>
            ) : (
              <><strong>{totalActions}</strong> changes ready · {skipCount} unchanged</>
            )}
            {hasConflict && <ConflictText> — two addresses point at the same unit; fix before applying.</ConflictText>}
          </FooterInfo>
          <FooterActions>
            <GhostButton
              type='button'
              onClick={
                publicDataStep
                  ? () => setPublicDataStep(false)
                  : reviewStep
                    ? () => setReviewStep(false)
                    : onClose
              }
              disabled={confirming || updatingIgnored}
            >
              {publicDataStep || reviewStep ? 'Back' : 'Cancel'}
            </GhostButton>
            {publicDataStep ? (
              <DangerButton
                type='button'
                onClick={handleUsePublicData}
                disabled={hasConflict || publicDataActionCount === 0 || confirming}
              >
                {confirming ? (
                  <>
                    <CircularProgress size={16} style={{ color: '#fff' }} /> Saving…
                  </>
                ) : (
                  'Use public data'
                )}
              </DangerButton>
            ) : (
              <PrimaryButton
                type='button'
                data-tour-id={reviewStep ? 'units.reconcile-apply' : 'units.reconcile-continue'}
                onClick={reviewStep ? handleConfirm : () => setReviewStep(true)}
                disabled={hasConflict || totalActions === 0 || confirming}
              >
                {confirming ? (
                  <>
                    <CircularProgress size={16} style={{ color: '#fff' }} /> Saving…
                  </>
                ) : (
                  reviewStep ? 'Apply changes' : 'Continue to review'
                )}
              </PrimaryButton>
            )}
          </FooterActions>
        </DialogFooter>
      )}
    </Dialog>
  )
}

export default UnitsReconciliationModal

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 18px 22px 14px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  @media (max-width: 760px) {
    padding: 11px 12px 9px;
  }

  @media (max-height: 650px) {
    align-items: center;
    padding-top: 7px;
    padding-bottom: 7px;
  }
`

const HeaderLeft = styled.div`
  flex: 1;
`

const DialogTitle = styled.h2`
  font-size: 21px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;

  @media (max-width: 760px) {
    font-size: 18px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #374151;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  touch-action: manipulation;
`

const DialogBody = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 14px 22px;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 760px) {
    display: block;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 10px 12px 18px;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  }

  @media (max-height: 650px) {
    padding-top: 7px;
    padding-bottom: 7px;
  }
`

const ResolveStep = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  gap: 12px;

  @media (max-width: 760px) {
    display: block;
  }
`

const ResolveControls = styled.div`
  flex-shrink: 0;
`

const RowsArea = styled.div`
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 760px) {
    display: block;
    overflow: visible;
    margin-top: 14px;
  }
`

const ViewTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-height: 650px) {
    margin-bottom: 5px;
  }
`

const ViewTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 1px 10px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#1eb8ce' : 'transparent')};
  background: transparent;
  color: ${({ $active }) => ($active ? '#1295a8' : '#64748b')};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  span {
    min-width: 22px;
    padding: 2px 7px;
    border-radius: 999px;
    background: ${({ $active }) => ($active ? '#e6fafd' : '#f1f5f9')};
    color: ${({ $active }) => ($active ? '#1295a8' : '#64748b')};
    font-size: 11px;
    text-align: center;
  }

  &:hover {
    color: #1295a8;
  }

  @media (max-height: 650px) {
    padding-top: 4px;
    padding-bottom: 6px;
  }
`

const ListTools = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  @media (max-width: 760px) {
    align-items: stretch;
    flex-direction: column;
  }

  @media (min-width: 380px) and (max-width: 760px) {
    align-items: center;
    flex-direction: row;
  }
`

const SearchField = styled.label`
  width: min(440px, 100%);
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid #dbe7f0;
  border-radius: 8px;
  background: #fff;
  color: #9ca3af;

  &:focus-within {
    border-color: #1eb8ce;
    box-shadow: 0 0 0 3px rgba(30, 184, 206, 0.1);
  }

  svg {
    flex-shrink: 0;
    font-size: 19px;
  }

  input {
    width: 100%;
    min-width: 0;
    padding: 10px 0;
    border: none;
    outline: none;
    background: transparent;
    color: #1f2937;
    font-size: 13px;

    &::placeholder {
      color: #9ca3af;
    }
  }

  @media (min-width: 380px) and (max-width: 760px) {
    flex: 1;
    width: auto;
  }

  @media (max-height: 650px) {
    input {
      padding-top: 7px;
      padding-bottom: 7px;
    }
  }
`

const SearchResultCount = styled.span`
  flex-shrink: 0;
  padding: 3px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const ApplySuggestionsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 9px 13px;
  border: 1px solid #1eb8ce;
  border-radius: 8px;
  background: #ecfeff;
  color: #1295a8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #d9fbff;
  }

  svg {
    font-size: 17px;
  }
`

const IgnoreAllButton = styled(ApplySuggestionsButton)`
  border-color: #cbd5e1;
  background: #fff;
  color: #475569;

  &:hover:not(:disabled) {
    background: #f8fafc;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
`

const RestoreAllButton = styled(IgnoreAllButton)`
  color: #1295a8;
`

const SuggestionsApplied = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  color: #15803d;
  font-size: 12px;
  font-weight: 700;

  svg {
    font-size: 17px;
  }
`

const Centered = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
`

const MutedText = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const RowList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 8px;
  scrollbar-gutter: stable;

  @media (max-width: 760px) {
    overflow: visible;
    padding-bottom: 12px;
    touch-action: pan-y;
  }

  &::-webkit-scrollbar {
    width: 9px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: #b9c7d4;
    border: 2px solid #f1f5f9;
    border-radius: 999px;
  }
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 4px;
  border-bottom: 1px solid #e5e7eb;
  border-left: 3px solid ${({ $flag }) => ($flag === 'bad' ? '#b00020' : 'transparent')};
  background: ${({ $flag }) => ($flag === 'bad' ? '#fdf6f6' : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 760px) {
    flex-direction: column;
    gap: 10px;
    padding: 12px 2px;
  }

  @media (min-width: 380px) and (max-width: 760px) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 8px 2px;
  }

  @media (max-height: 650px) and (min-width: 761px) {
    gap: 12px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`

const RowTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const STATUS_COLOURS = {
  correct: { bg: '#dcfce7', fg: '#15803d' },
  mismatch: { bg: '#fdecea', fg: '#b00020' },
  assign: { bg: '#fef3c7', fg: '#d97706' },
  unmatched: { bg: '#f1f5f9', fg: '#64748b' },
  fallback: { bg: '#eef2ff', fg: '#4f46e5' },
  create: { bg: '#ecfeff', fg: '#1295a8' },
  conflict: { bg: '#fdecea', fg: '#b00020' },
  ignored: { bg: '#f1f5f9', fg: '#475569' }
}

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $status }) => (STATUS_COLOURS[$status] || STATUS_COLOURS.unmatched).bg};
  color: ${({ $status }) => (STATUS_COLOURS[$status] || STATUS_COLOURS.unmatched).fg};
`

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
`

const RowAddress = styled.div`
  margin-top: 3px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.35;
  overflow-wrap: anywhere;

  @media (min-width: 380px) and (max-width: 760px) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 7px;

  @media (max-height: 650px) {
    margin-top: 3px;
  }
`

const Pill = styled.span`
  font-size: 12px;
  color: #1eb8ce;
  font-weight: 600;
`

const RowUprn = styled.span`
  font-size: 12px;
  color: #9ca3af;
`

const PostcodeLookupSection = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(360px, 0.9fr);
  gap: 8px 24px;
  align-items: center;
  padding: 4px 0 10px;
  margin-bottom: 2px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 760px) {
    padding: 2px 0 10px;
    margin-bottom: 0;
  }
`

const PostcodeLookupCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;

  strong {
    color: #1a1a2e;
    font-size: 14px;
    font-weight: 700;
  }

  @media (max-height: 650px) {
    span {
      display: none;
    }
  }
`

const PostcodeLookupControls = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;

  @media (max-width: 760px) {
    flex-direction: column;
  }

  @media (min-width: 380px) and (max-width: 760px) {
    flex-direction: row;
  }
`

const PostcodeInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid #dbe7f0;
  border-radius: 8px;
  padding: 10px 13px;
  color: #1f2937;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #1eb8ce;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }
`

const LookupPostcodesButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  background: #1eb8ce;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #b8dfe5;
    cursor: not-allowed;
  }

  touch-action: manipulation;
`

const PostcodeError = styled.div`
  grid-column: 2;
  color: #b00020;
  font-size: 12px;

  @media (max-width: 900px) {
    grid-column: 1;
  }
`

const PostcodeHelper = styled.div`
  grid-column: 2;
  color: ${({ $error }) => ($error ? '#b00020' : '#6b7280')};
  font-size: 11px;
  line-height: 1.4;

  @media (max-width: 900px) {
    grid-column: 1;
  }
`

const RowControl = styled.div`
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;

  .MuiAutocomplete-root {
    background: #fff;
  }

  .MuiOutlinedInput-root {
    min-height: 42px;
    padding: 2px 38px 2px 11px !important;
    border-radius: 8px;

    input {
      padding: 7px 0 !important;
      color: #1f2937;
      font-size: 13px;
      font-weight: 500;
    }

    fieldset {
      border-color: #dbe3ea;
    }

    &:hover fieldset {
      border-color: #aebbc7;
    }

    &.Mui-focused fieldset {
      border-width: 1px;
      border-color: #1eb8ce;
      box-shadow: 0 0 0 3px rgba(30, 184, 206, 0.1);
    }
  }

  .MuiAutocomplete-endAdornment {
    right: 7px;
  }

  .MuiAutocomplete-popupIndicator {
    width: 28px;
    height: 28px;
    color: #64748b;

    &:hover {
      background: #eef7f8;
    }
  }

  @media (max-width: 760px) {
    width: 100%;
    touch-action: manipulation;
  }

  @media (min-width: 380px) and (max-width: 760px) {
    width: 44%;
  }
`

const RestoreAddressButton = styled.button`
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 13px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #1eb8ce;
    background: #ecfeff;
    color: #1295a8;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }

  svg {
    font-size: 18px;
  }
`

const DropdownOption = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: ${({ $action }) => ($action ? '#334155' : '#1f2937')};
    font-size: 13px;
    font-weight: ${({ $action }) => ($action ? 600 : 500)};
    line-height: 1.3;
    white-space: normal;
  }

  span {
    color: #94a3b8;
    font-size: 11px;
    line-height: 1.25;
  }
`

const UseSuggested = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #1eb8ce;
  background: #ecfeff;
  color: #1295a8;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #d9fbff;
  }
  touch-action: manipulation;
`

const RowWarn = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: ${({ $error }) => ($error ? '#b00020' : '#d97706')};
`

const EmptyRow = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-size: 14px;
  color: #9ca3af;
`

const ReviewStep = styled.div`
  max-width: 720px;
  width: 100%;
  margin: auto;
  padding: 20px 0;

  @media (max-width: 760px) {
    margin: 0;
    padding: 8px 0 18px;
  }
`

const ReviewTitle = styled.h3`
  margin: 0 0 6px;
  color: #1a1a2e;
  font-size: 20px;
`

const ReviewIntro = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 13px;
`

const OutcomeList = styled.div`
  border-top: 1px solid #e5e7eb;
`

const OutcomeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 0;
  border-bottom: 1px solid #e5e7eb;

  div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    color: #1a1a2e;
    font-size: 14px;
  }

  span {
    color: #6b7280;
    font-size: 12px;
  }
`

const OutcomeIcon = styled.span`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $tone }) => ($tone === 'success' ? '#dcfce7' : $tone === 'create' ? '#ecfeff' : $tone === 'danger' ? '#fee2e2' : '#f1f5f9')};
  color: ${({ $tone }) => ($tone === 'success' ? '#15803d' : $tone === 'create' ? '#1295a8' : $tone === 'danger' ? '#b91c1c' : '#64748b')};
  font-size: 17px;
  font-weight: 700;

  svg {
    font-size: 17px;
  }
`

const ReviewError = styled.div`
  margin-top: 18px;
  padding: 12px 14px;
  border-left: 3px solid #b00020;
  background: #fdf6f6;
  color: #b00020;
  font-size: 13px;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 22px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.06);
  flex-shrink: 0;
  gap: 16px;

  @media (max-width: 760px) {
    align-items: center;
    gap: 8px;
    padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
  }

  @media (max-height: 650px) {
    padding-top: 6px;
    padding-bottom: calc(6px + env(safe-area-inset-bottom));
  }
`

const FooterInfo = styled.div`
  font-size: 13px;
  color: #6b7280;

  strong {
    color: #1a1a2e;
    font-weight: 700;
  }

  @media (max-width: 760px) {
    display: none;
  }
`

const ConflictText = styled.span`
  color: #b00020;
  font-weight: 600;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 760px) {
    width: 100%;
    gap: 8px;

    > button {
      flex: 1;
      min-width: 0;
    }
  }
`

const GhostButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1eb8ce;
  border: none;
  color: #fff;
  padding: 10px 26px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #17a0b4;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const DangerButton = styled(PrimaryButton)`
  background: #b00020;

  &:hover:not(:disabled) {
    background: #96001b;
  }
`

const PublicDataSection = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  gap: 8px 24px;
  align-items: center;
  padding: 4px 0 10px;
  margin-bottom: 2px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 760px) {
    padding: 2px 0 10px;
    margin-bottom: 0;
  }
`

const PublicDataCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;

  strong {
    color: #1a1a2e;
    font-size: 14px;
    font-weight: 700;
  }

  @media (max-height: 650px) {
    span {
      display: none;
    }
  }
`

const UsePublicDataButton = styled.button`
  justify-self: end;
  background: #fff;
  border: 1px solid #1eb8ce;
  color: #1295a8;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #ecfeff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 900px) {
    justify-self: start;
  }
`

const DeleteNamesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  max-height: 140px;
  overflow-y: auto;
`

const DeleteNameChip = styled.span`
  padding: 3px 10px;
  border: 1px solid #fecaca;
  border-radius: 999px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
`
